import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Branding, PageState, Quote, QuoteRequest, UserDetails, UserSetupInfo, LLMResponse, Parameter, ParameterValue } from "../types";
import quoteService, { SendQuoteParameterResponse } from "../services/quoteService";
import accountService from "../services/accountService";
import { useHistory } from "react-router-dom";
interface QuoteControllerContextType {
    branding: Partial<Branding>;
    userDetails: Partial<UserDetails>;
    userSetupInfo: Partial<UserSetupInfo>;
    quoteInProgress: Partial<QuoteRequest>;
    lastQuote: Partial<Quote>;
    pageState: Partial<PageState>;
    initialUrlParams: URLSearchParams;

    loadNewContent: (action: string) => void;
    getQuote: () => Promise<void>;
    setParam: (paramName: string, paramValue: any) => void;
    setPersistentParam: (paramName: string, paramValue: any) => void;
    removeParam: (paramName: string) => void;
    clearParams: () => void;
    sendQuoteParameter: (parameter: Parameter, value: any, checkNonsense: boolean) => Promise<SendQuoteParameterResponse>;
    redirect: (url: string) => void;

    quoteErrors: string[];
    isLoading: boolean;
}

type Param = {
    [key: string]: any;
};

const QuoteControllerContext = createContext<
    QuoteControllerContextType | undefined
>(undefined);

export const QuoteControllerContextProvider = ({ children }) => {
    const [initialUrlParams, setInitialUrlParams ] = useState<URLSearchParams>();
    const params = useRef<Param>({});
    const persistentParams = useRef<Param>({});

    const [branding, setBranding] = useState<Partial<Branding>>({});
    const [userSetupInfo, setUserSetupInfo] = useState<Partial<UserSetupInfo>>(null);
    const [userDetails, setUserDetails] = useState(null);
    const [quoteInProgress, setQuoteInProgress] = useState<Partial<QuoteRequest>>(null);
    const [lastQuote, setLastQuote] = useState<Partial<Quote>>(null);
    const [pageState, setPageState] = useState<Partial<PageState>>(null);
    const [quoteErrors, setQuoteErrors] = useState<string[]>([]);

    const [isLoading, setIsLoading] = useState(true);

    const history = useHistory();

    // INIT
    useEffect(() => {
        const init = async () => {
            const initParams = {};

            //Get adminId parameter from URL params.
            const urlParams = new URLSearchParams(window.location.search);
            setInitialUrlParams(urlParams);

            const adminIdParam = urlParams.get("adminid");
            const parsedAdminId = parseInt(adminIdParam, 10);
            if (!isNaN(parsedAdminId)) {
                initParams["adminid"] = parsedAdminId;
                setPersistentParam("adminid", parsedAdminId);
            }

            //Get Quote salt parameter from URL params.
            if (urlParams.get("resumequote")) {
                const quotesalt = urlParams.get("resumequote");
                initParams["resumequote"] = quotesalt;
                setPersistentParam("resumequote", quotesalt);
            }

            if (urlParams.get("covertype")) {
                const coverType = urlParams.get("covertype").toUpperCase();
                initParams["covertype"] = coverType;
            }

            if (urlParams.get("ref")) {
                const shortRef = urlParams.get("ref");
                initParams["ref"] = shortRef; 
            }

            //Get initial information from server.
            setIsLoading(true);
            const initResponse = await quoteService.initQuote(initParams);
            setIsLoading(false);

            if (!initResponse) throw new Error("Could not initialize quote.");

            initResponse.branding && setBranding(initResponse.branding);
            initResponse.userSetupInfo && setUserSetupInfo(initResponse.userSetupInfo);
            initResponse.userDetails && setUserDetails(initResponse.userDetails);
            initResponse.quoteInProgress && setQuoteInProgress(initResponse.quoteInProgress);
            initResponse.lastQuote && setLastQuote(initResponse.lastQuote);
            initResponse.pageState && setPageState(initResponse.pageState);
        };

        init();
    }, []);

    const setParam = useCallback(
        (name, value) => {
            if (!name && value !== null && value !== undefined) return;

            params.current = {
                ...params.current,
                [name]: value,
            };
        },
        [params],
    );

    const setPersistentParam = useCallback(
        (name, value) => {
            if (!name && value !== null && value !== undefined) return;
            persistentParams.current = {
                ...persistentParams.current,
                [name]: value,
            };
        },
        [persistentParams],
    );

    const removeParam = useCallback(
        (name: string) => {
            if (!name) return;

            const { [name]: _, ...rest } = params.current; // Destructure and exclude the key
            params.current = rest; // Update params with the remaining keys
        },
        [params],
    );

    //Reset all params
    const clearParams = useCallback(() => {
        params.current = {};
    }, []);

    const sendQuoteParameter = useCallback(async (parameter: Parameter, value: any, checkNonsense: boolean = false): Promise<SendQuoteParameterResponse> => {
        if (!parameter) return;

        const data = {
            label: parameter.label,
            dataType: parameter.dataTypeStr,
            value,
            checkNonsense
        }
        const response = await quoteService.sendQuoteParameter(data);
        return response
    }, []);

    const loadNewContent = useCallback(
        async (action: string) => {
            const data = { ...params.current, ...persistentParams.current };

            setIsLoading(true);
            const contentResponse = await quoteService.loadNewContent(action, data);
            setIsLoading(false);

            if (!contentResponse) return;

            contentResponse.userSetupInfo && setUserSetupInfo(contentResponse.userSetupInfo);
            contentResponse.userDetails && setUserDetails(contentResponse.userDetails);
            contentResponse.quoteInProgress && setQuoteInProgress(contentResponse.quoteInProgress);
            contentResponse.lastQuote && setLastQuote(contentResponse.lastQuote);
            contentResponse.pageState && setPageState(contentResponse.pageState);

            if (contentResponse.token) 
                accountService.logInWithToken(contentResponse.token, contentResponse.userDetails);

            //Only reset params if its not a reload
            if (action !== "reload")
                clearParams();
        },
        [clearParams, setQuoteErrors],
    );

    const getQuote = useCallback(async () => {
        const newParams = { ...params.current };

        setIsLoading(true);
        const response = await quoteService.getQuote(newParams);
        setIsLoading(false);

        clearParams();

        const { pageState, lastQuote } = response;

        lastQuote && setLastQuote(lastQuote);
        pageState && setPageState(pageState);
    }, []);

    const redirect = useCallback((url: string) => {
        history.push(url);
    }, [history]);

    const contextValue: QuoteControllerContextType = {
        loadNewContent,
        getQuote,
        setParam,
        setPersistentParam,
        removeParam,
        clearParams,
        sendQuoteParameter,
        redirect,
        isLoading,
        branding,
        userDetails,
        userSetupInfo,
        quoteInProgress,
        lastQuote,
        quoteErrors,
        pageState,
        initialUrlParams
    };

    return (
        <QuoteControllerContext.Provider value={contextValue}>
            {children}
        </QuoteControllerContext.Provider>
    );
};

export const useQuoteController = ():
    | QuoteControllerContextType
    | undefined => {
    return useContext(QuoteControllerContext);
};


